import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Channel, ContactResponse } from '@outreach/contracts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from '@/components/ui/sonner';
import { api, ApiRequestError } from '@/lib/api';
import {
  useConsentHistory,
  useDeleteContact,
  useRecordConsent,
  useResubscribe,
  useWithdraw,
} from './useContacts';

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'destructive' | 'muted'> = {
  eligible: 'success',
  no_consent: 'muted',
  no_destination: 'muted',
  consent_withdrawn: 'warning',
  suppressed_unsubscribe: 'destructive',
  suppressed_complaint: 'destructive',
  suppressed_permanent_bounce: 'destructive',
  suppressed_manual: 'destructive',
  invalid_destination: 'destructive',
};

function nowLocal(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ContactDrawer({
  contact,
  onClose,
}: {
  contact: ContactResponse | null;
  onClose: () => void;
}) {
  return (
    <Sheet open={contact !== null} onOpenChange={(o) => !o && onClose()}>
      <SheetContent>
        {contact && <ContactDrawerBody contact={contact} onClose={onClose} />}
      </SheetContent>
    </Sheet>
  );
}

function ContactDrawerBody({
  contact,
  onClose,
}: {
  contact: ContactResponse;
  onClose: () => void;
}) {
  const history = useConsentHistory(contact.id);
  const timeline = useQuery({
    queryKey: ['contact-timeline', contact.id],
    queryFn: () => api.contacts.timeline(contact.id),
  });
  const record = useRecordConsent(contact.id);
  const resubscribe = useResubscribe(contact.id);
  const withdraw = useWithdraw(contact.id);
  const remove = useDeleteContact();

  const [channel, setChannel] = useState<Channel>('email');
  const [level, setLevel] = useState<'single_optin' | 'double_optin'>('single_optin');
  const [scope] = useState('outreach');
  const [source, setSource] = useState('');
  const [evidenceRef, setEvidenceRef] = useState('');
  const [obtainedAt, setObtainedAt] = useState(nowLocal());
  const [error, setError] = useState<string | null>(null);

  const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || 'Contact';

  async function onRecord(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await record.mutateAsync({
        channel,
        level,
        scope,
        source,
        evidenceRef,
        obtainedAt: new Date(obtainedAt).toISOString(),
        ...(level === 'double_optin' ? { confirmedAt: new Date(obtainedAt).toISOString() } : {}),
      });
      toast.success('Consent recorded');
      setSource('');
      setEvidenceRef('');
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(
          err.code === 'conflict'
            ? 'This endpoint opted out — use Resubscribe with fresh evidence.'
            : err.message,
        );
      } else {
        setError('Something went wrong.');
      }
    }
  }

  async function onResubscribe(ch: Channel) {
    try {
      await resubscribe.mutateAsync({
        channel: ch,
        scope: 'outreach',
        source: source || 'operator resubscribe',
        evidenceRef: evidenceRef || 'operator resubscribe',
        obtainedAt: new Date().toISOString(),
      });
      toast.success('Resubscribed');
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.message : 'Could not resubscribe');
    }
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle>{name}</SheetTitle>
        <SheetDescription>Contact details, consent and activity.</SheetDescription>
      </SheetHeader>

      <div className="space-y-1 text-sm">
        <Row label="Email" value={contact.email ?? '—'} />
        <Row label="Phone" value={contact.phone ?? '—'} />
      </div>

      <Separator />

      <div className="space-y-2">
        <p className="text-sm font-medium">Channel status</p>
        <div className="space-y-1.5">
          {contact.channels.map((c) => (
            <div key={c.channel} className="flex items-center justify-between">
              <span className="text-sm capitalize">{c.channel}</span>
              <div className="flex items-center gap-2">
                <Badge variant={STATUS_VARIANT[c.status] ?? 'muted'}>
                  {c.status.replace(/_/g, ' ')}
                </Badge>
                {c.eligible && (
                  <Button size="sm" variant="ghost" onClick={() => withdraw.mutate(c.channel)}>
                    Withdraw
                  </Button>
                )}
                {(c.status === 'consent_withdrawn' ||
                  c.status === 'suppressed_unsubscribe' ||
                  c.status === 'suppressed_manual') && (
                  <Button size="sm" variant="ghost" onClick={() => onResubscribe(c.channel)}>
                    Resubscribe
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      <form className="space-y-3" onSubmit={onRecord}>
        <p className="text-sm font-medium">Record existing permission</p>
        <div className="grid grid-cols-2 gap-2">
          <NativeSelect
            label="Channel"
            value={channel}
            onChange={(v) => setChannel(v as Channel)}
            options={[
              ['email', 'Email'],
              ['whatsapp', 'WhatsApp'],
            ]}
          />
          <NativeSelect
            label="Level"
            value={level}
            onChange={(v) => setLevel(v as 'single_optin' | 'double_optin')}
            options={[
              ['single_optin', 'Single opt-in'],
              ['double_optin', 'Double opt-in'],
            ]}
          />
        </div>
        <LabeledInput label="Source" value={source} onChange={setSource} required />
        <LabeledInput
          label="Evidence reference"
          value={evidenceRef}
          onChange={setEvidenceRef}
          required
        />
        <div className="space-y-1.5">
          <Label htmlFor="obtainedAt">Obtained at</Label>
          <Input
            id="obtainedAt"
            type="datetime-local"
            value={obtainedAt}
            onChange={(e) => setObtainedAt(e.target.value)}
          />
        </div>
        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        <Button type="submit" size="sm" disabled={record.isPending}>
          {record.isPending ? 'Saving…' : 'Record consent'}
        </Button>
      </form>

      <Separator />

      <div className="space-y-2">
        <p className="text-sm font-medium">Consent history</p>
        {history.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {history.data && history.data.items.length === 0 && (
          <p className="text-sm text-muted-foreground">No consent records yet.</p>
        )}
        <ol className="space-y-1 text-sm text-muted-foreground">
          {history.data?.items.map((r) => (
            <li key={r.id}>
              {new Date(r.recordedAt).toLocaleString()} — {r.channel} · {r.action} ({r.level})
            </li>
          ))}
        </ol>
      </div>

      <Separator />

      <div className="space-y-2">
        <p className="text-sm font-medium">Activity</p>
        {timeline.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
        {timeline.data && timeline.data.entries.length === 0 && (
          <p className="text-sm text-muted-foreground">No activity yet.</p>
        )}
        <ol className="space-y-1 text-sm text-muted-foreground">
          {timeline.data?.entries.map((e, i) => (
            <li key={`${e.at}-${i}`} className="flex items-center gap-2">
              <span>
                {new Date(e.at).toLocaleString()} — {e.kind.replace(/_/g, ' ')} · {e.source}
              </span>
              {e.isTest && <Badge variant="muted">test</Badge>}
            </li>
          ))}
        </ol>
      </div>

      <Separator />

      <Button
        variant="outline"
        size="sm"
        className="w-fit"
        onClick={async () => {
          await remove.mutateAsync({ id: contact.id, mode: 'archive' });
          toast.success('Contact archived');
          onClose();
        }}
      >
        Archive contact
      </Button>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span>{value}</span>
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  const id = label.replace(/\s+/g, '-').toLowerCase();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} required={required} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function NativeSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<[string, string]>;
}) {
  const id = label.toLowerCase();
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </div>
  );
}
